import { useEffect, useState } from "react";
import { Link } from "@/i18n/LangRouting";
import { ArrowRight, ExternalLink } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { LangButton } from "@/visitor/Header";
import { Footer } from "@/visitor/SignupPage";
import { usePageTitle, formatDate } from "@/lib/pageTitle";

function fmtDates(e, t, lang) {
  const f = (d) => formatDate(d, lang);
  if (e.start_date && e.end_date) return `${f(e.start_date)} – ${f(e.end_date)}`;
  if (e.end_date) return `${t("open_guide")} → ${f(e.end_date)}`;
  if (e.type === "permanent") return t("open_all_year");
  return e.start_date ? f(e.start_date) : "";
}

function ExhibitionCard({ e, t, lang }) {
  return (
    <article className="border border-[var(--line)] flex flex-col fade-up" data-testid={`home-exhibition-${e.slug}`}>
      <div className="relative aspect-[4/3] overflow-hidden">
        {e.cover_url ? <img src={fileUrl(e.cover_url)} alt={e.name} className="w-full h-full object-cover" /> : <div className="art-placeholder w-full h-full">[Exhibition photo]</div>}
        <span className={`tag absolute top-3 left-3 ${e.type === "permanent" ? "tag-permanent" : "tag-touring"}`} data-testid={`home-exhibition-type-${e.slug}`}>{t(e.type === "permanent" ? "permanent" : "touring")}</span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-display text-3xl uppercase leading-none">{e.name}</h3>
        {e.subtitle && <p className="text-sm text-[#d8d8d8] mt-2">{e.subtitle}</p>}
        <p className="text-xs text-[var(--muted)] mt-1" data-testid={`home-exhibition-dates-${e.slug}`}>{fmtDates(e, t, lang)}</p>
        <div className="flex gap-2 mt-5 pt-2 mt-auto">
          <Link to={`/e/${e.slug}`} className="btn-accent flex-1 py-3 text-xs text-center" data-testid={`home-open-guide-${e.slug}`}>{t("open_guide")}</Link>
          {e.ticket_url && <a href={e.ticket_url} target="_blank" rel="noreferrer" className="btn-outline flex-1 py-3 text-xs text-center" data-testid={`home-tickets-${e.slug}`}>{t("tickets")}</a>}
        </div>
      </div>
    </article>
  );
}

export default function Home() {
  const { t, lang } = useI18n();
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/public/exhibitions").then((r) => setList(r.data.filter((e) => e.show_on_home))).catch(() => setList([])); }, []);
  usePageTitle(t("site_name"));
  const anyGifts = !!list?.some((e) => e.gifts?.enabled);
  const discount = list?.find((e) => e.gifts?.enabled)?.gifts?.discount_percent || 25;

  return (
    <div className="min-h-screen" data-testid="home-page">
      <header className="flex items-center justify-between px-6 lg:px-12 h-20 max-w-6xl mx-auto">
        <div className="font-display uppercase leading-none">
          <span className="text-xl block">{t("site_name")}</span>
          <span className="text-[0.6rem] tracking-[0.3em] text-[var(--muted)] font-sans">{t("free_audio_guide")}</span>
        </div>
        <LangButton />
      </header>
      <section className="relative grain px-6 lg:px-12 pt-10 pb-14 max-w-6xl mx-auto">
        <p className="text-xs sm:text-sm tracking-[0.3em] uppercase font-bold">{t("home_kicker")}</p>
        <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl uppercase leading-[0.9] text-accent mt-1" data-testid="home-title">{t("home_title")}</h1>
        <p className="text-[var(--muted)] mt-6 max-w-lg text-sm sm:text-base">{t("home_intro")}</p>
      </section>
      <section id="now-on-view" className="px-6 lg:px-12 max-w-6xl mx-auto scroll-mt-6">
        <h2 className="text-xs tracking-[0.3em] uppercase text-[var(--muted)] mb-5">{t("now_on_view")}</h2>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="home-exhibition-grid">
          {list?.map((e) => <ExhibitionCard key={e.slug} e={e} t={t} lang={lang} />)}
          {list?.length === 0 && <p className="text-[var(--muted)] text-sm">{t("no_exhibitions")}</p>}
        </div>
      </section>
      <section className="px-6 lg:px-12 max-w-6xl mx-auto mt-14">
        <div className="border-2 border-[var(--accent)] p-7 sm:p-10 sm:flex sm:items-center sm:gap-10" data-testid={anyGifts ? "home-gift-cta" : "home-newsletter-cta"}>
          <div className="flex-1">
            <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none">{t(anyGifts ? "gift_headline" : "home_newsletter_headline", { discount })}</h2>
            <p className="text-[var(--muted)] mt-3">{t(anyGifts ? "gift_sub" : "home_newsletter_sub")}</p>
          </div>
          <Link to="/signup" className="btn-accent inline-flex items-center justify-center gap-3 px-8 py-4 mt-6 sm:mt-0 w-full sm:w-auto" data-testid="home-get-gift">{t(anyGifts ? "nudge_cta" : "home_newsletter_cta")} <ArrowRight size={18} /></Link>
        </div>
      </section>
      <section className="px-6 lg:px-12 max-w-6xl mx-auto mt-6">
        <a href="https://artful-merchandise.com" target="_blank" rel="noreferrer" className="card-row flex items-center justify-between gap-4 bg-[var(--surface)] p-7 sm:p-10" data-testid="home-shop-link">
          <div>
            <h2 className="font-display text-3xl uppercase leading-none">{t("shop_title")}</h2>
            <p className="text-[var(--muted)] mt-2 text-sm">{t("shop_sub")}</p>
          </div>
          <ExternalLink size={22} className="text-accent flex-none" />
        </a>
      </section>
      <div className="px-6 lg:px-12 max-w-6xl mx-auto py-12"><Footer t={t} /></div>
    </div>
  );
}
